import { priceEngine } from '@/services/price-engine'
import type { QuoteDocument } from '@/modules/quotes/types'

import { SnapshotCompare } from './SnapshotCompare'
import { SnapshotService, snapshotService } from './SnapshotService'
import { SnapshotVersion } from './SnapshotVersion'
import type {
  CreateSnapshotInput,
  QuoteSnapshot,
  SnapshotCompareResult,
  SnapshotPermissions,
  SnapshotSearchFilters,
  SnapshotValidityDays,
} from './types'
import { DEFAULT_SNAPSHOT_PERMISSIONS as DEFAULT_PERMS } from './types'

/**
 * SnapshotEngine — facade for immutable quote photographs.
 * Prepared for Master Search, Financial Panel, Opportunities, CRM and AI.
 */
export class SnapshotEngine {
  private readonly service: SnapshotService
  private readonly compareEngine = new SnapshotCompare()
  private permissions: SnapshotPermissions = { ...DEFAULT_PERMS }

  constructor(service: SnapshotService = snapshotService) {
    this.service = service
  }

  setPermissions(permissions: Partial<SnapshotPermissions>): SnapshotPermissions {
    this.permissions = { ...this.permissions, ...permissions }
    return this.permissions
  }

  getPermissions(): SnapshotPermissions {
    return { ...this.permissions }
  }

  assertPermission(action: keyof SnapshotPermissions): void {
    if (!this.permissions[action]) {
      throw new Error(`Permissão negada: ${action}`)
    }
  }

  createFromQuote(
    quote: QuoteDocument,
    options?: {
      lockedPrice?: boolean
      notes?: string
      createdBy?: string
      validUntilDays?: SnapshotValidityDays
    },
  ): QuoteSnapshot {
    this.assertPermission('create')

    const lines = quote.items.map((item) =>
      priceEngine.calculateLine(
        {
          id: item.id,
          quantity: item.quantity,
          purchasePrice: item.purchasePrice,
          operational: item.operational,
          taxes: item.taxes,
          profit: item.profit,
          salePrice: item.salePrice,
        },
        'components',
      ),
    )
    const summary = priceEngine.summarize(
      lines,
      quote.discountPercent,
      quote.discountValue,
      quote.freight,
    )

    const input: CreateSnapshotInput = {
      quoteId: quote.id,
      quoteCode: quote.code,
      quoteTitle: quote.title,
      customerId: quote.customerId,
      customerName: quote.customerName,
      validUntilDays: options?.validUntilDays ?? quote.validUntilDays,
      lockedPrice: options?.lockedPrice ?? false,
      notes: options?.notes ?? quote.notes,
      createdBy: options?.createdBy,
      discountPercent: quote.discountPercent,
      discountValue: quote.discountValue,
      freight: quote.freight,
      totals: {
        purchaseTotal: summary.purchaseTotal,
        saleTotal: summary.finalPrice,
        profitTotal: summary.profitNet,
        margin: summary.marginPercent,
      },
      items: quote.items.map((item) => ({
        productId: item.productId,
        description: item.description,
        purchasePrice: item.purchasePrice,
        salePrice: item.salePrice,
        operationalCost: item.operational,
        tax: item.taxes,
        profit: item.profit,
        margin: item.salePrice > 0 ? Math.round(((item.profit / item.salePrice) * 100 + Number.EPSILON) * 100) / 100 : 0,
        quantity: item.quantity,
        pixPrice: item.purchasePrice,
        cardPrice: item.salePrice,
        installmentPrice: item.salePrice,
      })),
      priceEnginePayload: priceEngine.prepareSnapshotPayload(summary),
    }

    return this.service.create(input)
  }

  /**
   * Creates a new version only when content differs from the latest snapshot.
   */
  captureIfChanged(quote: QuoteDocument, options?: Parameters<SnapshotEngine['createFromQuote']>[1]): QuoteSnapshot | null {
    const latest = this.service.getLatest(quote.id)
    if (!latest) return this.createFromQuote(quote, options)

    const fingerprint = this.fingerprintQuote(quote)
    const latestFingerprint = this.fingerprintSnapshot(latest)
    if (!SnapshotVersion.isRelevantChange(latestFingerprint, fingerprint)) {
      return null
    }
    return this.createFromQuote(quote, options)
  }

  listByQuote(quoteId: string): QuoteSnapshot[] {
    this.assertPermission('view')
    return this.service.listByQuote(quoteId)
  }

  getById(id: string): QuoteSnapshot | null {
    this.assertPermission('view')
    return this.service.getById(id)
  }

  getLatest(quoteId: string): QuoteSnapshot | null {
    this.assertPermission('view')
    return this.service.getLatest(quoteId)
  }

  compare(leftId: string, rightId: string): SnapshotCompareResult {
    this.assertPermission('view')
    const left = this.service.getById(leftId)
    const right = this.service.getById(rightId)
    if (!left || !right) throw new Error('Snapshots para comparação não encontrados')
    return this.compareEngine.compare(left, right)
  }

  compareWithQuote(leftId: string, quote: QuoteDocument): SnapshotCompareResult {
    this.assertPermission('view')
    const left = this.service.getById(leftId)
    if (!left) throw new Error('Snapshot não encontrado')
    const right = this.createEphemeralFromQuote(quote)
    return this.compareEngine.compare(left, right)
  }

  updateSelectedItems(
    leftId: string,
    rightId: string,
    itemKeys: string[] | 'all',
    createNewVersion: boolean,
  ): QuoteSnapshot | null {
    this.assertPermission('edit')
    if (!createNewVersion) return null
    const right = this.service.getById(rightId)
    if (!right) throw new Error('Snapshot atual não encontrado')
    return this.service.applyRightItemsAsNewVersion(leftId, right, itemKeys)
  }

  updateSelectedFromQuote(
    leftId: string,
    quote: QuoteDocument,
    itemKeys: string[] | 'all',
    createNewVersion: boolean,
  ): QuoteSnapshot | null {
    this.assertPermission('edit')
    if (!createNewVersion) return null
    const right = this.createEphemeralFromQuote(quote)
    return this.service.applyRightItemsAsNewVersion(leftId, right, itemKeys)
  }

  duplicate(snapshotId: string): QuoteSnapshot {
    this.assertPermission('create')
    return this.service.duplicate(snapshotId)
  }

  setLocked(snapshotId: string, locked: boolean): QuoteSnapshot {
    this.assertPermission('edit')
    return this.service.setLocked(snapshotId, locked)
  }

  search(filters: SnapshotSearchFilters): QuoteSnapshot[] {
    this.assertPermission('view')
    return this.service.search(filters)
  }

  listHistory(snapshotId?: string) {
    this.assertPermission('view')
    return this.service.listHistory(snapshotId)
  }

  exportJson(snapshotId: string) {
    this.assertPermission('view')
    return this.service.exportJson(snapshotId)
  }

  exportPdf(snapshotId: string) {
    this.assertPermission('view')
    return this.service.exportPdfMeta(snapshotId)
  }

  importJson(payload: unknown): QuoteSnapshot {
    this.assertPermission('create')
    return this.service.importPayload(payload)
  }

  remove(snapshotId: string): void {
    this.assertPermission('delete')
    this.service.remove(snapshotId)
  }

  /** Future integrations stubs */
  prepareForMasterSearch(snapshot: QuoteSnapshot) {
    return { channel: 'master-search', snapshotId: snapshot.id, items: snapshot.items }
  }

  prepareForFinancialPanel(snapshot: QuoteSnapshot) {
    return { channel: 'financial-panel', snapshotId: snapshot.id, totals: snapshot.totals }
  }

  prepareForOpportunities(snapshot: QuoteSnapshot) {
    return { channel: 'opportunities', snapshotId: snapshot.id, customerId: snapshot.customerId }
  }

  prepareForCrm(snapshot: QuoteSnapshot) {
    return { channel: 'crm', snapshotId: snapshot.id, customerId: snapshot.customerId, code: snapshot.quoteCode }
  }

  prepareForAi(snapshot: QuoteSnapshot) {
    return { channel: 'ai', snapshotId: snapshot.id, fingerprint: this.fingerprintSnapshot(snapshot) }
  }

  resetMemory(): void {
    this.service.resetMemory()
  }

  private createEphemeralFromQuote(quote: QuoteDocument): QuoteSnapshot {
    const createdAt = new Date().toISOString()
    return {
      id: `ephemeral-${quote.id}`,
      quoteId: quote.id,
      version: 0,
      versionLabel: 'atual',
      status: 'ACTIVE',
      createdBy: 'live-quote',
      createdAt,
      validUntil: createdAt,
      validityDays: quote.validUntilDays,
      lockedPrice: false,
      notes: quote.notes,
      customerId: quote.customerId,
      customerName: quote.customerName,
      quoteCode: quote.code,
      quoteTitle: quote.title,
      discountPercent: quote.discountPercent,
      discountValue: quote.discountValue,
      freight: quote.freight,
      totals: {
        purchaseTotal: quote.totals.purchaseTotal,
        saleTotal: quote.totals.total,
        profitTotal: quote.totals.profitTotal,
        margin: quote.totals.margin,
      },
      items: quote.items.map((item) => ({
        id: item.id,
        snapshotId: `ephemeral-${quote.id}`,
        productId: item.productId,
        description: item.description,
        supplier: null,
        supplierCode: null,
        supplierLink: null,
        purchasePrice: item.purchasePrice,
        pixPrice: item.purchasePrice,
        cardPrice: item.salePrice,
        installmentPrice: item.salePrice,
        salePrice: item.salePrice,
        operationalCost: item.operational,
        tax: item.taxes,
        profit: item.profit,
        margin: item.salePrice > 0 ? Math.round(((item.profit / item.salePrice) * 100 + Number.EPSILON) * 100) / 100 : 0,
        quantity: item.quantity,
        stock: null,
        currency: 'BRL',
        searchDate: null,
        createdAt,
      })),
    }
  }

  private fingerprintQuote(quote: QuoteDocument): string {
    return JSON.stringify({
      items: quote.items.map((i) => ({
        productId: i.productId,
        description: i.description,
        quantity: i.quantity,
        purchasePrice: i.purchasePrice,
        salePrice: i.salePrice,
        profit: i.profit,
        operational: i.operational,
        taxes: i.taxes,
      })),
      discountPercent: quote.discountPercent,
      discountValue: quote.discountValue,
      freight: quote.freight,
      customerId: quote.customerId,
    })
  }

  private fingerprintSnapshot(snapshot: QuoteSnapshot): string {
    return JSON.stringify({
      items: snapshot.items.map((i) => ({
        productId: i.productId,
        description: i.description,
        quantity: i.quantity,
        purchasePrice: i.purchasePrice,
        salePrice: i.salePrice,
        profit: i.profit,
        operational: i.operationalCost,
        taxes: i.tax,
      })),
      discountPercent: snapshot.discountPercent,
      discountValue: snapshot.discountValue,
      freight: snapshot.freight,
      customerId: snapshot.customerId,
    })
  }
}

export const snapshotEngine = new SnapshotEngine()
